import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { receiptApi, tenantApi } from '../services/api';
import { formatCurrency } from '../utils/currency';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const TenantYearlySummary = () => {
  const { tenantId } = useParams();
  const [summaries, setSummaries] = useState([]);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [tenantRes, summaryRes] = await Promise.all([
          tenantApi.getById(tenantId),
          receiptApi.getYearlySummary(tenantId),
        ]);
        setTenant(tenantRes.data);
        setSummaries(summaryRes.data);
      } catch (error) {
        toast.error('Failed to load yearly summary');
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [tenantId]);

  const currency = tenant?.currency || 'USD';

  const grandTotal = summaries.reduce((acc, s) => acc + s.totalAmount, 0);

  return (
    <div>
      <div className="flex items-center mb-6">
        <Link
          to={`/tenants/${tenantId}/receipts`}
          className="text-gray-600 hover:text-gray-900 mr-3"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </Link>
        <div>
          {tenant && (
            <h1 className="text-2xl font-bold text-gray-900">
              Yearly Summary — {tenant.firstName} {tenant.lastName}
            </h1>
          )}
          <p className="text-gray-600 text-sm">Total paid per calendar year based on period start date</p>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8">Loading...</div>
      ) : summaries.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-600">No receipts found for this tenant.</p>
        </div>
      ) : (
        <div className="bg-white shadow sm:rounded-md overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Year</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Receipts</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Basic Rent</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Charges</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {summaries.map((s) => (
                <tr key={s.year} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">{s.year}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{s.receiptCount}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{formatCurrency(s.totalBasicRent, currency)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{formatCurrency(s.totalCharges, currency)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">{formatCurrency(s.totalAmount, currency)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50 border-t-2 border-gray-300">
              <tr>
                <td className="px-6 py-3 text-sm font-semibold text-gray-900">All years</td>
                <td className="px-6 py-3 text-sm text-gray-500 text-right">
                  {summaries.reduce((acc, s) => acc + s.receiptCount, 0)}
                </td>
                <td className="px-6 py-3 text-sm font-semibold text-gray-900 text-right">
                  {formatCurrency(summaries.reduce((acc, s) => acc + s.totalBasicRent, 0), currency)}
                </td>
                <td className="px-6 py-3 text-sm font-semibold text-gray-900 text-right">
                  {formatCurrency(summaries.reduce((acc, s) => acc + s.totalCharges, 0), currency)}
                </td>
                <td className="px-6 py-3 text-sm font-bold text-gray-900 text-right">{formatCurrency(grandTotal, currency)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};

export default TenantYearlySummary;
